import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  uploadToStorage,
  removeFromStorage,
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_VIDEO_FORMATS,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
} from "../utils/fileUpload";
import {
  applyScheduledDaysToCreatives,
  removeScheduledDaysForUrl,
  setScheduledDaysForUrl,
} from "@/utils/creativeScheduleStorage";
import type { Creative } from "../types";

/* -------------------------------------------------------------------------- */
/*                        useMediaOwnerCreativeUpload                         */
/* -------------------------------------------------------------------------- */

/**
 * Manages per-location creative uploads (multiple files per location).
 * Now keyed by location name instead of media owner.
 * Alan locations require both portrait AND landscape orientations.
 */
export const useMediaOwnerCreativeUpload = () => {
  const { toast } = useToast();

  // Record<locationName, Creative[]>
  const [mediaOwnerCreatives, setMediaOwnerCreatives] = useState<Record<string, Creative[]>>(() => {
    try {
      const saved = localStorage.getItem("mediaOwnerCreatives");
      const parsed = (saved ? JSON.parse(saved) : {}) as Record<string, Creative[]>;

      // Merge in any scheduledDays that were written synchronously by URL
      for (const key of Object.keys(parsed)) {
        parsed[key] = applyScheduledDaysToCreatives(parsed[key] || []);
      }

      return parsed;
    } catch {
      return {};
    }
  });

  // Per-location uploading flags
  const [uploadingMap, setUploadingMap] = useState<Record<string, boolean>>({});
  const isUploading = Object.values(uploadingMap).some(Boolean);

  // Persist
  useEffect(() => {
    localStorage.setItem("mediaOwnerCreatives", JSON.stringify(mediaOwnerCreatives));
  }, [mediaOwnerCreatives]);

  /* ------------------------------ Validation ------------------------------ */

  const validateFile = useCallback(
    (file: File): boolean => {
      const isImage = SUPPORTED_IMAGE_FORMATS.includes(file.type);
      const isVideo = SUPPORTED_VIDEO_FORMATS.includes(file.type) || file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        toast({
          title: "Unsupported file type",
          description: "Please upload an image (JPG, PNG, GIF) or video (MP4, MOV).",
          variant: "destructive",
        });
        return false;
      }

      if (isImage && file.size > MAX_IMAGE_SIZE) {
        toast({
          title: "Image too large",
          description: "Images must be smaller than 10 MB.",
          variant: "destructive",
        });
        return false;
      }

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        toast({
          title: "Video too large",
          description: "Videos must be smaller than 100 MB.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    },
    [toast],
  );

  /* ------------------------------- Sanitize -------------------------------- */

  const sanitize = (name: string) =>
    name
      .replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, "")
      .replace(/[^\w.-]/g, "_")
      .replace(/_{2,}/g, "_")
      .replace(/^_+|_+$/g, "") || "creative";

  /* -------------------------------- Upload -------------------------------- */

  const handleMediaOwnerFileChange = useCallback(
    async (locationName: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      console.log(`[locationUpload] Processing ${files.length} files for location: ${locationName}`);
      if (files.length === 0) return;

      // Orientation may be attached by caller for Alan locations
      const orientation: "portrait" | "landscape" | undefined = (e.target as any).orientation;
      const existing = mediaOwnerCreatives[locationName] || [];

      // Alan-specific: allow one file per orientation (check mediaOwner from existing creatives or target)
      const isAlan = (e.target as any).isAlan;
      if (isAlan && orientation) {
        if (existing.some((c) => c.orientation === orientation)) {
          toast({
            title: "Limit reached",
            description: `You already have a ${orientation} creative for this location. Remove it first.`,
            variant: "destructive",
          });
          return;
        }
      }

      // Validate all files first
      const validFiles = files.filter(file => validateFile(file));
      console.log(`[locationUpload] ${validFiles.length} valid files after validation`);
      if (validFiles.length === 0) return;

      setUploadingMap((prev) => ({ ...prev, [locationName]: true }));

      try {
        const uploadedCreatives: Creative[] = [];
        
        for (const file of validFiles) {
          const orientationSuffix = orientation ? `-${orientation}` : "";
          // Do NOT embed day into filename - user must choose the day after upload
          const fileName = `${Date.now()}-${sanitize(locationName)}${orientationSuffix}-${sanitize(file.name)}`;
          const url = await uploadToStorage(file, fileName);

          uploadedCreatives.push({
            name: file.name,
            type: file.type,
            size: file.size,
            url,
            locationName,
            orientation,
            scheduledDays: [], // Don't default to day 1 - user must choose
          });
        }

        setMediaOwnerCreatives((prev) => {
          const existingCreatives = prev[locationName] || [];
          console.log(`[locationUpload] Adding ${uploadedCreatives.length} to existing ${existingCreatives.length} creatives`);
          return {
            ...prev,
            [locationName]: [...existingCreatives, ...uploadedCreatives],
          };
        });

        toast({ title: "Uploaded", description: `${validFiles.length} file(s) uploaded successfully.` });
      } catch (err) {
        console.error("[locationUpload] Error:", err);
        toast({
          title: "Upload failed",
          description: err instanceof Error ? err.message : "Unknown error – please retry.",
          variant: "destructive",
        });
      } finally {
        setUploadingMap((prev) => ({ ...prev, [locationName]: false }));
        if (e.target) e.target.value = "";
      }
    },
    [mediaOwnerCreatives, toast, validateFile],
  );

  /* -------------------------------- Remove -------------------------------- */

  const removeMediaOwnerCreative = useCallback(
    async (locationName: string, index: number) => {
      const arr = mediaOwnerCreatives[locationName] || [];
      const creative = arr[index];
      if (!creative) return;

      // Keep schedule storage in sync
      if (creative.url) removeScheduledDaysForUrl(creative.url);

      setUploadingMap((prev) => ({ ...prev, [locationName]: true }));

      try {
        // Only remove from storage if it's not an external link
        if (creative.url && !creative.isExternalLink) {
          await removeFromStorage(creative.url);
        }

        setMediaOwnerCreatives((prev) => {
          const updated = [...(prev[locationName] || [])];
          updated.splice(index, 1);
          return { ...prev, [locationName]: updated };
        });

        toast({ title: "Removed", description: "Creative removed." });
      } catch (err) {
        console.error("[locationUpload] Remove error:", err);
        toast({
          title: "Remove failed",
          description: err instanceof Error ? err.message : "Could not remove file.",
          variant: "destructive",
        });
      } finally {
        setUploadingMap((prev) => ({ ...prev, [locationName]: false }));
      }
    },
    [mediaOwnerCreatives, toast],
  );

  /* ------------------------------ URL Submit ------------------------------ */

  const handleMediaOwnerUrlSubmit = useCallback(
    (locationName: string, url: string, orientation?: "portrait" | "landscape") => {
      const existing = mediaOwnerCreatives[locationName] || [];

      // Alan-specific: allow one file per orientation (check if isAlan based on existing creatives)
      const isAlan = existing.some((c) => c.orientation !== undefined);
      if (isAlan && orientation) {
        if (existing.some((c) => c.orientation === orientation)) {
          toast({
            title: "Limit reached",
            description: `You already have a ${orientation} creative for this location. Remove it first.`,
            variant: "destructive",
          });
          return;
        }
      }

      // Extract a display name from the URL
      let name = 'External Link';
      try {
        const urlObj = new URL(url);
        // Use hostname as name for clarity (e.g., "drive.google.com", "dropbox.com")
        name = urlObj.hostname.replace('www.', '');
      } catch {
        // Keep default name if URL parsing fails
      }

      setMediaOwnerCreatives((prev) => ({
        ...prev,
        [locationName]: [
          ...(prev[locationName] || []),
          {
            name,
            type: 'link', // Mark as link type
            size: 0,
            url,
            locationName,
            orientation,
            isExternalLink: true, // Flag as external link
            scheduledDays: [], // Don't default to day 1 - user must choose
          },
        ],
      }));

      toast({ title: "Added", description: `Link added successfully.` });
    },
    [mediaOwnerCreatives, toast],
  );

  /* ---------------------- Update Creative Scheduled Days ------------------- */

  const updateMediaOwnerCreativeDays = useCallback(
    (locationName: string, creativeIndex: number, days: number[]) => {
      setMediaOwnerCreatives((prev) => {
        const existing = prev[locationName] || [];
        if (creativeIndex < 0 || creativeIndex >= existing.length) return prev;

        const updated = [...existing];
        const url = updated[creativeIndex]?.url;

        updated[creativeIndex] = {
          ...updated[creativeIndex],
          scheduledDays: days,
        };

        // Persist by URL immediately to avoid races with effects
        if (url) setScheduledDaysForUrl(url, days);

        return { ...prev, [locationName]: updated };
      });
    },
    [],
  );

  /* ---------------------- Force Sync to LocalStorage ---------------------- */
  
  const syncToLocalStorage = useCallback(() => {
    localStorage.setItem("mediaOwnerCreatives", JSON.stringify(mediaOwnerCreatives));
    console.log('[useMediaOwnerCreativeUpload] Forced sync to localStorage:', JSON.stringify(mediaOwnerCreatives));
  }, [mediaOwnerCreatives]);

  /* ------------------------------ Return API ------------------------------ */

  return {
    mediaOwnerCreatives,
    isUploading,
    uploadingMap,
    handleMediaOwnerFileChange,
    removeMediaOwnerCreative,
    handleMediaOwnerUrlSubmit,
    updateMediaOwnerCreativeDays,
    syncToLocalStorage,
  };
};
